import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { StudentLayout } from '../components/StudentLayout';
import { useToast } from '../components/Toast';
import type { ExamSummary } from '../types/models';

type Tab = 'exams' | 'results' | 'reeval';

const COMPLETED = new Set(['Submitted', 'AutoSubmitted', 'Disqualified']);
const isCompleted = (e: ExamSummary) => !!e.attemptStatus && COMPLETED.has(e.attemptStatus);
const startMs = (e: ExamSummary) => new Date(e.scheduledStart).getTime();
const endMs = (e: ExamSummary) => new Date(e.scheduledEnd).getTime();

/** Letter grade from a score percentage (Indian-college style). */
function gradeOf(score: number, total: number): string {
  if (!total) return '';
  const p = (score / total) * 100;
  if (p >= 90) return 'A+';
  if (p >= 80) return 'A';
  if (p >= 70) return 'B+';
  if (p >= 60) return 'B';
  if (p >= 50) return 'C';
  if (p >= 40) return 'D';
  return 'F';
}

/** "2d 4h" / "6h 22m" / "12m" from a future duration in ms. */
function fmtDuration(ms: number): string {
  const mins = Math.max(0, Math.round(ms / 60000));
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** "In 2 days" / "In 5 hours" / "Soon" badge from a future duration in ms. */
function fmtBadge(ms: number): string {
  const mins = Math.round(ms / 60000);
  const d = Math.floor(mins / 1440);
  const h = Math.floor(mins / 60);
  if (d >= 1) return `In ${d} day${d === 1 ? '' : 's'}`;
  if (h >= 1) return `In ${h} hour${h === 1 ? '' : 's'}`;
  return 'Soon';
}

// ---- tiny inline icons (render everywhere, no emoji/font dependency) ----
type IconProps = { size?: number };
const svg = (size: number, ...children: ReactNode[]) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>{children}</svg>
);
const IconBolt = ({ size = 18 }: IconProps) => svg(size, <path key="p" d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />);
const IconCalendar = ({ size = 18 }: IconProps) => svg(size,
  <rect key="r" x="3" y="4" width="18" height="18" rx="2" />, <path key="a" d="M16 2v4M8 2v4M3 10h18" />);
const IconCheck = ({ size = 18 }: IconProps) => svg(size,
  <path key="c" d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />, <path key="t" d="m22 4-10 10.01-3-3" />);
const IconClock = ({ size = 18 }: IconProps) => svg(size,
  <circle key="c" cx="12" cy="12" r="9" />, <path key="h" d="M12 7v5l3 2" />);
const IconList = ({ size = 16 }: IconProps) => svg(size,
  <path key="l" d="M8 6h13M8 12h13M8 18h13" />, <path key="d" d="M3 6h.01M3 12h.01M3 18h.01" />);
const IconAward = ({ size = 16 }: IconProps) => svg(size,
  <circle key="c" cx="12" cy="8" r="6" />, <path key="r" d="M8.5 13.5 7 22l5-3 5 3-1.5-8.5" />);

/** code · type · batch subtitle + subject/title heading for an exam card. */
function headingFor(e: ExamSummary) {
  const heading = e.subjectName || e.title;
  const typeLabel = e.subjectName ? e.title : null; // the exam "type" sits in the subtitle
  const subtitle = [e.subjectCode || e.batchCode, typeLabel, e.batchName].filter(Boolean).join(' · ');
  return { heading, subtitle };
}

function ExamMeta({ e }: { e: ExamSummary }) {
  return (
    <div className="exam-card-meta">
      <span><IconList /> {e.questionCount} questions</span>
      <span><IconAward /> {e.totalMarks} marks</span>
      <span><IconClock size={16} /> {e.durationMinutes} min</span>
    </div>
  );
}

function Badge({ kind, children }: { kind: string; children: ReactNode }) {
  return <span className={`exam-badge ${kind}`}>{children}</span>;
}

/** Shell for one exam row: heading + subtitle + body + right-side action. */
function ExamCard({ e, accent, badge, children, action }: {
  e: ExamSummary; accent?: boolean; badge?: ReactNode; children?: ReactNode; action?: ReactNode;
}) {
  const { heading, subtitle } = headingFor(e);
  return (
    <div className={`exam-card${accent ? ' accent' : ''}`}>
      <div className="exam-card-head">
        <div className="exam-card-titles">
          {subtitle && <div className="exam-card-sub">{subtitle}</div>}
          <div className="exam-card-title">{heading}</div>
        </div>
        {badge}
      </div>
      <div className="exam-card-body">
        <div className="exam-card-left">{children}</div>
        {action && <div className="exam-card-action">{action}</div>}
      </div>
    </div>
  );
}

function StatCard({ icon, value, label }: { icon: ReactNode; value: number; label: string }) {
  return (
    <div className="stat-card">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{icon} {label}</div>
    </div>
  );
}

export function StudentDashboardPage({ tab = 'exams' }: { tab?: Tab }) {
  const toast = useToast();
  const [exams, setExams] = useState<ExamSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAllPast, setShowAllPast] = useState(false);
  // "Reset exam" is a dev-only convenience (backend 404s it outside Development).
  const [devEnabled, setDevEnabled] = useState(false);

  useEffect(() => {
    api.listExams().then(setExams).catch(e => setError(e?.message ?? 'Failed to load exams.'));
    api.devEnabled().then(r => setDevEnabled(r.enabled)).catch(() => { });
  }, []);

  // Delete my attempt (and its answers) for an exam so I can take it again.
  async function resetAttempt(examId: string) {
    if (!confirm('Reset this exam? This deletes your attempt and all your answers so you can take it again.')) return;
    try {
      await api.resetMyAttempt(examId);
      setExams(await api.listExams());
      toast.success('Exam reset — you can take it again.');
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to reset the exam.');
    }
  }

  // Small "Reset attempt" control, shown only in dev and only when I actually have an attempt.
  const resetBtn = (e: ExamSummary) =>
    devEnabled && e.attemptStatus ? (
      <div className="exam-card-foot">
        <button className="ghost-btn" style={{ color: 'var(--danger)' }}
          onClick={() => resetAttempt(e.id)}
          title="Delete your attempt and answers so you can take this exam again">
          ↺ Reset attempt
        </button>
      </div>
    ) : null;

  const now = Date.now();
  const groups = useMemo(() => {
    const published = (exams ?? []).filter(e => e.status === 'Published');
    const active = published.filter(e => !isCompleted(e) && startMs(e) <= now && endMs(e) >= now);
    const upcoming = published.filter(e => startMs(e) > now)
      .sort((a, b) => startMs(a) - startMs(b));
    const past = published.filter(e => isCompleted(e) || (endMs(e) < now && !active.includes(e)))
      .filter(e => startMs(e) <= now)
      .sort((a, b) => endMs(b) - endMs(a));
    const attempted = published.filter(isCompleted);
    const resultsPending = attempted.filter(e => !e.resultsPublished);
    return { published, active, upcoming, past, attempted, resultsPending };
  }, [exams, now]);

  // ---------- per-state card renderers ----------
  const activeCard = (e: ExamSummary) => {
    const over = endMs(e) <= now;
    return (
      <ExamCard key={e.id} e={e} accent badge={<Badge kind="active">● Active</Badge>}
        action={over
          ? <Badge kind="missed">Time over</Badge>
          : <Link to={`/exam/${e.id}`}><button className="primary">▶ {e.attemptStatus === 'InProgress' ? 'Resume exam' : 'Start exam'}</button></Link>}>
        <ExamMeta e={e} />
        <div className="exam-card-foot">
          <span className="muted">Window closes in</span>{' '}
          <strong>{fmtDuration(endMs(e) - now)}</strong>
        </div>
        {resetBtn(e)}
      </ExamCard>
    );
  };

  const upcomingCard = (e: ExamSummary) => (
    <ExamCard key={e.id} e={e} badge={<Badge kind="soon">{fmtBadge(startMs(e) - now)}</Badge>}
      action={<button disabled title="Opens at the scheduled time">Not yet open</button>}>
      <ExamMeta e={e} />
      <div className="exam-card-foot">
        <span className="muted">Opens</span> <strong>{fmtDuration(startMs(e) - now)}</strong>
      </div>
    </ExamCard>
  );

  const pastCard = (e: ExamSummary) => {
    // Result is out.
    if (e.resultsPublished) {
      if (e.attemptStatus === 'Disqualified') {
        return (
          <ExamCard key={e.id} e={e} badge={<Badge kind="fail">Result out</Badge>}
            action={<Link to={`/exam/${e.id}/result`}><button>View details</button></Link>}>
            <div className="exam-card-foot"><strong className="score-fail">Disqualified</strong></div>
            {resetBtn(e)}
          </ExamCard>
        );
      }
      const score = e.scoreAwarded ?? 0;
      const pct = e.totalMarks ? Math.round((score / e.totalMarks) * 100) : 0;
      const pass = pct >= 40;
      const grade = gradeOf(score, e.totalMarks);
      return (
        <ExamCard key={e.id} e={e} badge={<Badge kind="result">Result out</Badge>}
          action={<Link to={`/exam/${e.id}/result`}><button>View details</button></Link>}>
          <div className="score-row">
            <span className="muted">Score</span>
            <div className="score-bar">
              <div className="score-bar-fill" style={{ width: `${pct}%`, background: pass ? 'var(--success)' : 'var(--danger)' }} />
            </div>
            <span className={pass ? 'score-pass' : 'score-fail'}>{score} / {e.totalMarks} · {grade}</span>
          </div>
          {resetBtn(e)}
        </ExamCard>
      );
    }
    // Submitted, awaiting a grade / result.
    if (isCompleted(e)) {
      return (
        <ExamCard key={e.id} e={e} badge={<Badge kind="pending">Grading in progress</Badge>}>
          <div className="exam-card-foot muted">
            Submitted · {e.resultPublishAt ? `Result on ${new Date(e.resultPublishAt).toLocaleDateString()}` : 'Result expected soon'}
          </div>
          {resetBtn(e)}
        </ExamCard>
      );
    }
    // Window closed, never attempted.
    return (
      <ExamCard key={e.id} e={e} badge={<Badge kind="missed">Missed</Badge>}>
        <div className="exam-card-foot muted">Window closed · Not attempted</div>
        {resetBtn(e)}
      </ExamCard>
    );
  };

  // ---------- tab bodies ----------
  if (error) return <StudentLayout><div className="card error">{error}</div></StudentLayout>;
  if (!exams) return <StudentLayout><div className="card muted">Loading…</div></StudentLayout>;

  let body: ReactNode;

  if (tab === 'exams') {
    const past = showAllPast ? groups.past : groups.past.slice(0, 4);
    body = (
      <>
        <div className="dash-stats">
          <StatCard icon={<IconBolt size={15} />} value={groups.active.length} label="Active now" />
          <StatCard icon={<IconCalendar size={15} />} value={groups.upcoming.length} label="Upcoming" />
          <StatCard icon={<IconCheck size={15} />} value={groups.attempted.length} label="Attempted" />
          <StatCard icon={<IconClock size={15} />} value={groups.resultsPending.length} label="Results pending" />
        </div>

        {groups.active.length > 0 && (
          <section className="dash-section">
            <h2>Active now</h2>
            <div className="exam-card-list">{groups.active.map(activeCard)}</div>
          </section>
        )}

        {groups.upcoming.length > 0 && (
          <section className="dash-section">
            <h2>Upcoming</h2>
            <div className="exam-card-list">{groups.upcoming.map(upcomingCard)}</div>
          </section>
        )}

        <section className="dash-section">
          <div className="dash-section-head">
            <h2>Past exams</h2>
            {groups.past.length > 4 && (
              <button className="ghost-btn" onClick={() => setShowAllPast(s => !s)}>
                {showAllPast ? 'Show less' : 'See all'}
              </button>
            )}
          </div>
          {groups.past.length === 0
            ? <div className="card muted">No past exams yet.</div>
            : <div className="exam-card-list">{past.map(pastCard)}</div>}
        </section>
      </>
    );
  } else if (tab === 'results') {
    const withResults = groups.attempted;
    body = (
      <section className="dash-section">
        <h2>Results</h2>
        {withResults.length === 0
          ? <div className="card muted">No results yet — they appear here once you've attempted an exam.</div>
          : <div className="exam-card-list">{withResults
              .sort((a, b) => Number(b.resultsPublished) - Number(a.resultsPublished))
              .map(pastCard)}</div>}
      </section>
    );
  } else {
    // Re-evaluation: published-result exams where a student may ask for a re-check.
    const eligible = groups.attempted.filter(e => e.resultsPublished && e.attemptStatus !== 'Disqualified');
    body = (
      <section className="dash-section">
        <h2>Re-evaluation</h2>
        <div className="muted" style={{ marginBottom: 12 }}>
          Request a re-check of a published result. Open the result to submit your reason.
        </div>
        {eligible.length === 0
          ? <div className="card muted">No results are available for re-evaluation yet.</div>
          : <div className="exam-card-list">{eligible.map(e => {
              const score = e.scoreAwarded ?? 0;
              const grade = gradeOf(score, e.totalMarks);
              return (
                <ExamCard key={e.id} e={e} badge={<Badge kind="result">Result out</Badge>}
                  action={<Link to={`/exam/${e.id}/result`}><button>Request re-eval</button></Link>}>
                  <div className="exam-card-foot">
                    <span className="muted">Score</span> <strong>{score} / {e.totalMarks} · {grade}</strong>
                  </div>
                </ExamCard>
              );
            })}</div>}
      </section>
    );
  }

  return <StudentLayout>{body}</StudentLayout>;
}
